import { syncPersonImageId, clearSelectedVTOResult } from '../slices/vtoSlice';
import { clearTryOnItems } from '../slices/clothingItemsSlice';
import { revertToOriginal } from '../slices/personImageSlice';

/**
 * Middleware to sync person image changes with VTO session
 * When the active person image changes, update the VTO session's personImageId
 * and clear any existing VTO state (results, wearing items)
 */
export const personImageVTOSyncMiddleware = (store) => (next) => (action) => {
  // Listen for person image changes
  if (action.type === 'personImage/setActivePersonImage') {
    const { personImageId } = action.payload;

    // Get CURRENT state BEFORE the action is processed
    const stateBefore = store.getState();
    const currentPersonImageId = stateBefore.personImage?.activePersonImageId;
    const currentSession = stateBefore.vto?.currentSession;
    const isShowingVTOResult = stateBefore.personImage?.isVirtualTryOnResult;

    // Clear VTO state if the person image is actually changing OR if a VTO result is currently shown
    // (since setActivePersonImage will revert the canvas to the person image)
    if ((currentPersonImageId && currentPersonImageId !== personImageId) || isShowingVTOResult) {
      console.log('[PersonImageSync] Person image changed or VTO was active - clearing VTO state', {
        from: currentPersonImageId,
        to: personImageId
      });

      // Clear wearing items from the "Wearing" column
      store.dispatch(clearTryOnItems());

      // Clear selected VTO result from history
      store.dispatch(clearSelectedVTOResult());

      // Clear VTO result from canvas - revert to showing just the person image
      store.dispatch(revertToOriginal());

      console.log('[PersonImageSync] VTO state cleared - canvas reverted to original person image');
    }

    // Process the action AFTER we've checked the before state
    const result = next(action);

    // Sync person image ID to VTO session if there's an active session
    if (currentSession && currentSession.personImageId !== personImageId) {
      console.log('[PersonImageSync] Syncing person image ID to VTO session:', personImageId);
      store.dispatch(syncPersonImageId({ personImageId }));
    }

    return result;
  }

  if (action.type === 'personImage/clearActivePersonImage' || action.type === 'personImage/revertToOriginal') {
    const stateBefore = store.getState();
    const isShowingVTOResult = stateBefore.personImage?.isVirtualTryOnResult;

    if (isShowingVTOResult || stateBefore.vto?.selectedVTOResultId) {
      console.log(`[PersonImageSync] ${action.type} - clearing VTO state`);
      if (action.type !== 'personImage/revertToOriginal') {
          // Revert to original is often called when removing a specific item, so we might want to 
          // let the caller handle clearing try on items, but to be safe and consistent with the look 
          // selection, we'll clear it. Actually, wait, CurrentlyWearingSection.js clears the specific item
          // and then calls revertToOriginal(). If we clear all try-on items here, we might clear items
          // the user didn't want to remove if they were just removing one.
          // Let's only clear the selected VTO result to keep the look selection in sync.
      }
      // Only clear the selected VTO result to keep the look selection in sync with the canvas.
      // We don't clear TryOnItems here because revertToOriginal is called when a single item is removed,
      // and we want the remaining items to stay in the 'Wearing' section.
      if (action.type === 'personImage/clearActivePersonImage') {
         store.dispatch(clearTryOnItems());
      }
      store.dispatch(clearSelectedVTOResult());
    }
    
    return next(action);
  }

  // Process other actions normally
  const result = next(action);

  // Also sync when VTO session starts - update person image slice
  if (action.type === 'vto/startVTOSession') {
    const { personImageId } = action.payload;
    const state = store.getState();
    const activePersonImageId = state.personImage?.activePersonImageId;

    // If VTO session starts with a different person image, log it
    if (personImageId !== activePersonImageId) {
      console.log('[PersonImageSync] VTO session started with person image:', personImageId);
    }
  }

  return result;
};

export default personImageVTOSyncMiddleware;
